import { useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { findRole } from 'src/utils/authHelper';
import { isPublicRoute } from 'src/utils/urlHelper';
import { GET_SESSION_QUERY } from 'src/apolloClient/graphql/queries/getSessionQuery';
import { throwGqlError } from 'src/utils/throwGqlError';
import { AppContext } from '../context';
import { getBaseUrl } from '../utils/getConfig';
import { UNAUTHORIZED_PAGE_URL } from 'src/constants/urlConstants';
import pollingHelper from 'src/utils/pollingHelper';
import { get } from 'lodash';

export const useSession = () => {
  const location = useLocation();
  const [getSession, { error, refetch, data = {}, loading }] =
    useLazyQuery(GET_SESSION_QUERY);

  const { setLoading, setCurrentAgent, setOriginalAgent, setAgency } =
    useContext(AppContext);

  useEffect(() => {
    if (!isPublicRoute(location.pathname)) getSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) throwGqlError(error);
  }, [error]);

  useEffect(() => {
    const currentAgency = data?.currentUser?.agent?.agency;
    const originalAgency = data?.originalUser?.agent?.agency;
    const isAbleToSet = !loading && !error && currentAgency && originalAgency;
    const isImpersonatingInsured =
      data.currentUser &&
      findRole(data.currentUser, 'insured') &&
      originalAgency;

    if (isImpersonatingInsured) {
      // this will happen when user impersonate insurer and manually navigate back to AD
      // in this case we will end the impersonation
      const endImpersonationUrl =
        window.config.backendApiUrl +
        '/admin/impersonates/finish?redirect_url=' +
        getBaseUrl();
      window.location.assign(endImpersonationUrl);
    } else if (data.currentUser && (!currentAgency || !originalAgency)) {
      window.location.assign(`${getBaseUrl()}${UNAUTHORIZED_PAGE_URL}`);
    }

    if (isAbleToSet) {
      setLoading(false);
      setCurrentAgent(data.currentUser.agent);
      setOriginalAgent(data.originalUser.agent);
      setAgency(data.currentUser.agent.agency);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data?.currentUser, data?.originalUser]);

  const pollingForAgencyStatus = async ({
    status,
    onSuccessCallback = () => {},
    onErrorCallback = () => {},
  }) => {
    try {
      await pollingHelper({
        requestMethod: refetch,
        exitCondition: response =>
          get(response, 'data.currentUser.agent.agency.status') === status,
        maxAttempts: 10,
      });
      onSuccessCallback();
    } catch (error) {
      onErrorCallback();
    }
  };

  return {
    loading,
    pollingForAgencyStatus,
    error,
    get data() {
      if (!data.currentUser) {
        return null;
      }

      const { currentUser, originalUser } = data;
      const isAgentImpersonated =
        (originalUser?.email && currentUser?.email) !== originalUser?.email;

      return {
        originalUser,
        currentUser: {
          ...currentUser,
          isAgentImpersonated,
          isSuperAdmin: findRole(currentUser, 'super_admin'),
        },
      };
    },
  };
};
